import { Fragment, useState } from 'react'

import { Combobox } from '@headlessui/react'
import { useDecision } from '@optimizely/react-sdk'
import FuzzySearch from 'fuzzy-search'

import { Member, Post, Space } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { FeaturesList } from '../common/constants/features.js'
import { useControlledHotkeys } from '../common/hooks/useControlledHotkeys.js'
import { useHotkeysSettings } from '../common/hooks/useHotkeysSettings.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { useMemberActionItems } from '../Member/hooks/useMemberActionItems.js'
import { usePostActionItems } from '../Post/hooks/usePostActionItems.js'
import { useSpaceActionItems } from '../Space/hooks/useSpaceActionItems.js'
import { CommandBarAction } from './CommandBarAction.js'
import { useCommandBar } from './CommandBarContext.js'
import { useCommandBarAdminSettings } from './hooks/useCommandBarAdminSettings.js'
import { useCommandBarNavigation } from './hooks/useCommandBarNavigation.js'
import { useCommandBarPosts } from './hooks/useCommandBarPosts.js'
import { useCommandBarSearch } from './hooks/useCommandBarSearch.js'
import { CommandBarActionType } from './types.js'

export const CommandBarHotKey = ({
  actionItem,
}: {
  actionItem: CommandBarActionType
}) => {
  const { push } = useRouter()

  useControlledHotkeys(actionItem?.hotkey, e => {
    if (!actionItem?.action && !actionItem?.url) {
      return undefined
    }
    e.preventDefault()
    if (typeof actionItem?.action === 'function') {
      return actionItem.action()
    }
    return push(actionItem?.url)
  })

  return null
}

export const CommandBar = () => {
  const { $t } = useI18n()
  const [rawQuery, setRawQuery] = useState('')
  const { push } = useRouter()
  const query = rawQuery.toLowerCase().replace(/^[#>]/, '')
  const {
    context: baseContext,
    open,
    close,
    isOpen,
    options: { context: customContext, excludeActions = [] },
  } = useCommandBar()
  const context = customContext ?? baseContext
  const { disabled: hotkeysDisabled } = useHotkeysSettings()

  const onClose = () => {
    close()
    setRawQuery('')
  }

  const runAction = actionItem => {
    onClose()
    if (typeof actionItem?.action === 'function') {
      actionItem.action()
      return
    }
    push(actionItem?.url)
  }

  const [decisionCommandBar] = useDecision(FeaturesList.CommandBar)

  useControlledHotkeys(
    'cmd+k,ctrl+k',
    e => {
      if (isOpen) {
        onClose()
      } else {
        open()
      }

      e.preventDefault()
    },
    { enabled: decisionCommandBar.enabled },
  )

  const { actions: postsActions } = useCommandBarPosts()
  const { actions: navigationActions } = useCommandBarNavigation()
  const { actions: adminSettingsActions } = useCommandBarAdminSettings()
  const { actions: searchActions } = useCommandBarSearch({
    query: rawQuery,
  })
  const contextPost = context.find(c => c?.entityType === 'post')
    ?.entity as Post
  const contextSpace = context.find(c => c?.entityType === 'space')
    ?.entity as Space
  const contextMember = context.find(c => c?.entityType === 'member')
    ?.entity as Member
  const {
    actions: postActions,
    shortcuts: postShortcuts,
    children: postChildren,
  } = usePostActionItems({
    post: contextPost ?? null,
    context: 'post',
  })

  const { actions: spaceActions, children: spaceChildren } =
    useSpaceActionItems({
      space: contextSpace ?? null,
    })

  const { actions: memberActions, children: memberChildren } =
    useMemberActionItems({
      member: contextMember ?? null,
      context: 'commandbar',
    })

  if (!decisionCommandBar.enabled) {
    return null
  }

  const entityActions = [
    ...postActions,
    ...(postShortcuts ?? []),
    ...memberActions,
    ...spaceActions,
  ]

  const actions: CommandBarActionType[] = [
    ...(excludeActions.includes('entity') ? [] : entityActions),
    ...(excludeActions.includes('posts') ? [] : postsActions),
    ...(excludeActions.includes('navigations') ? [] : navigationActions),
    ...(excludeActions.includes('adminSettings') ? [] : adminSettingsActions),
  ]

  const searcher = new FuzzySearch(
    actions.filter(action => !!action),
    ['name'],
    {
      caseSensitive: false,
    },
  )

  const filteredActions = query
    ? (searcher.search(query) as CommandBarActionType[])
    : actions

  const allActions = [
    ...filteredActions,
    ...(excludeActions.includes('search') ? [] : searchActions),
  ]
  // const sortedActions = filteredActions.sort((a, b) => {
  //   return a.category > b.category ? 1 : -1
  // })

  let lastCategory = null

  return (
    <>
      {actions
        .filter(action => action?.hotkey)
        .map(actionItem => (
          <CommandBarHotKey
            key={`${actionItem.name}-${actionItem.category}`}
            actionItem={actionItem}
          />
        ))}
      {isOpen && (
        <Modal open onClose={onClose} size="2xl">
          <Combobox onChange={item => runAction(item)} value={null}>
            {(!!contextPost || !!contextMember || !!contextSpace) && (
              <div className="p-3 pb-0">
                <Badge size="lg">
                  {contextPost?.title ||
                    contextMember?.name ||
                    contextSpace?.name}
                </Badge>
              </div>
            )}
            <div className="border-b border-line-subdued m-1">
              <Combobox.Input
                as={Input}
                leadingIcon={<SvgIcon name="search" />}
                showFocusRing={false}
                className="text-lg border-none"
                placeholder={$t({
                  id: 'Generics.SearchDotDotDot',
                  defaultMessage: 'Search...',
                })}
                onChange={event => setRawQuery(event.target.value)}
              />
            </div>
            {allActions.length > 0 && (
              <Combobox.Options
                static
                className="max-h-80 scroll-py-10 scroll-pb-2 overflow-y-auto py-4 pt-1"
              >
                {allActions.length > 0 && (
                  <>
                    {allActions.map((action, index) => {
                      if (!action) {
                        return null
                      }
                      let showCategory = false
                      if (lastCategory !== action?.category) {
                        showCategory = true
                        lastCategory = action?.category
                      }
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Fragment key={index}>
                          {showCategory && (
                            <li className="text-content-subdued px-4 text-sm pb-1 pt-2">
                              {action?.category}
                            </li>
                          )}
                          <CommandBarAction
                            action={action}
                            hideHotkey={hotkeysDisabled}
                          />
                        </Fragment>
                      )
                    })}
                  </>
                )}
              </Combobox.Options>
            )}
          </Combobox>
        </Modal>
      )}
      {postChildren}
      {spaceChildren}
      {memberChildren}
    </>
  )
}
